/**
 * PriorRoundsHistory — the locked history of a preparation request's earlier
 * rounds (CLAUDE.md §26.10), rendered read-only for every role above the
 * current round's task list in PrepRequestDetail.jsx. Extracted verbatim
 * (step P2 of the PrepRequestDetail decomposition, audit §6.2); the moved
 * block keeps its original indentation.
 */

import { Lock, CheckCircle2, Circle } from 'lucide-react';
import { formatDateTime } from '../../utils/dateHelpers';
import { formatPrepYmd } from '../../utils/prepRequests';
import { numberPrepTasks } from '../../utils/prepTasks';
import { nb } from './NotebookPaper';

export default function PriorRoundsHistory({ priorRounds }) {
  return (
    <>
        {/* Prior preparation rounds (§26.10) — permanent, locked records.
            Rendered read-only for everyone, whatever the request's current
            status: no tick buttons, no amendment, no reordering, ever. The
            padlock next to each round divider is the same locked cue the
            amendment view uses for completed tasks. */}
        {priorRounds.map((round) => (
          <div key={`round-${round.round}`}>
            <div style={nb.divider}>
              <Lock size={12} color="#4a6182" style={{ marginRight: '6px', flex: '0 0 auto' }} aria-hidden="true" />
              — Round {round.round}
              {round.completedAt ? ` · completed ${formatDateTime(round.completedAt)}` : ''}
              {round.appointmentDate ? ` · appt ${formatPrepYmd(round.appointmentDate)}` : ''} —
            </div>
            {(round.tasks || []).length === 0 && (
              <div style={{ ...nb.summaryText, color: '#8a7a55' }}>No tasks recorded in this round.</div>
            )}
            {numberPrepTasks(round.tasks || []).map(({ task: t, label, depth }, i) => {
              const TickIcon = t.completed ? CheckCircle2 : Circle;
              return (
                <div key={t.id || i} style={{ ...nb.taskRow, paddingLeft: depth === 1 ? '36px' : 0 }}>
                  <span style={{ ...nb.taskNum, minWidth: depth === 1 ? '40px' : '24px' }}>{label}</span>
                  <TickIcon
                    size={17}
                    color={t.completed ? '#2EC4B6' : '#a9b6c6'}
                    style={{ flex: '0 0 auto', alignSelf: 'center' }}
                  />
                  <span
                    style={{
                      ...nb.summaryText,
                      whiteSpace: 'pre-wrap',
                      textDecoration: t.completed ? 'line-through' : 'none',
                      color: t.completed ? 'var(--cp-text-muted)' : nb.summaryText.color,
                    }}
                  >
                    {t.text}
                  </span>
                </div>
              );
            })}
            {/* Group requests: that round's per-member prepared state,
                preserved and locked per member (§26.10). */}
            {round.memberSections && (round.memberSections.sections || []).length > 0 && (
              <div style={{ margin: '6px 0 2px' }}>
                {(round.memberSections.sections || []).map((sec) => (
                  <div
                    key={sec.reviewClientId}
                    style={{
                      fontFamily: "'Kalam', cursive",
                      fontSize: '12px',
                      color: sec.prepared ? 'var(--cp-success)' : 'var(--cp-text-muted)',
                      lineHeight: '20px',
                    }}
                  >
                    {sec.prepared ? '✓' : '○'} {sec.name} — {sec.prepared ? 'prepared' : 'not prepared'} in this round
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </>
  );
}
